import { createFileRoute, Link } from '@tanstack/react-router';

import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Markdown,
  PageHeader,
  PageLayout,
  Skeleton,
} from '@keboola/design';

import { QueryError } from '../../../../components/QueryError';
import { PublishRejectActions } from '../../../../features/admin/components/PublishRejectActions';
import { useAdminApp } from '../../../../features/admin/queries';
import { AppIcon } from '../../../../features/apps/components/AppIcon';
import { ComponentBadges } from '../../../../features/apps/components/ComponentBadges';
import { PublicFlags } from '../../../../features/apps/components/PublicFlags';
import { getAppTitle } from '../../../../features/apps/helpers';

export const Route = createFileRoute('/admin/components/$appId/')({
  component: AdminComponentDetailPage,
  head: () => ({ meta: [{ title: 'Component | Keboola Developer Portal admin' }] }),
});

function AdminComponentDetailPage() {
  const { appId } = Route.useParams();
  const { data: app, isLoading, isError, error, refetch } = useAdminApp(appId);

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageHeader.Detail
          breadcrumbs={
            <Breadcrumb>
              <Breadcrumb.List>
                <Breadcrumb.Item>
                  <Breadcrumb.Link asChild>
                    <Link to="/admin">Components</Link>
                  </Breadcrumb.Link>
                </Breadcrumb.Item>
                <Breadcrumb.Separator />
                <Breadcrumb.Item>
                  <Breadcrumb.Page>{app === undefined ? appId : getAppTitle(app)}</Breadcrumb.Page>
                </Breadcrumb.Item>
              </Breadcrumb.List>
            </Breadcrumb>
          }
          title={app === undefined ? appId : getAppTitle(app)}
          description={app?.shortDescription}
          titleIcon={app === undefined ? undefined : <AppIcon app={app} size={48} />}
          badges={app === undefined ? undefined : <ComponentBadges app={app} />}
          primaryAction={
            <Button asChild variant="primary">
              <Link to="/admin/components/$appId/edit" params={{ appId }}>
                Edit
              </Link>
            </Button>
          }
          secondaryAction={
            <Button asChild variant="secondary">
              <Link to="/admin/components/$appId/changes" params={{ appId }}>
                Changes
              </Link>
            </Button>
          }
        />
      </PageLayout.Header>

      {isLoading && <Skeleton className="tw:h-64" />}

      <QueryError
        isError={isError}
        hasData={app !== undefined}
        error={error}
        onRetry={() => void refetch()}
        loadTitle="Could not load this component"
        refreshTitle="Could not refresh this component"
        staleNote="Showing the version from the last successful load."
        notFound={{
          title: 'This component does not exist',
          description:
            'It may have been removed, or the link may be out of date. Browse the catalog to find what you are looking for.',
        }}
        titleAs="h2"
      />

      {app !== undefined && (
        <div className="tw:grid tw:gap-6 tw:lg:grid-cols-[2fr_1fr]">
          <div className="tw:flex tw:flex-col tw:gap-6">
            {app.publishingStatus === 'requested' && (
              <Alert variant="info" title="Publishing requested">
                {app.publishingRequest?.createdOn === undefined ||
                app.publishingRequest.createdOn === null
                  ? 'This component is waiting for a decision.'
                  : `Requested on ${new Date(app.publishingRequest.createdOn).toLocaleDateString()}.`}
              </Alert>
            )}
            {app.publishingStatus === 'rejected' &&
              typeof app.publishingRequest?.rejectionReason === 'string' && (
                <Alert variant="warning" title="Previously rejected">
                  {app.publishingRequest.rejectionReason}
                </Alert>
              )}

            <Card>
              <Card.Header>
                <Card.Title as="h2">Description</Card.Title>
              </Card.Header>
              <Card.Content>
                {app.longDescription === undefined ||
                app.longDescription === null ||
                app.longDescription === '' ? (
                  <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
                    This component has no long description.
                  </p>
                ) : (
                  <Markdown rhythm="chrome">{app.longDescription}</Markdown>
                )}
              </Card.Content>
            </Card>
          </div>

          <div className="tw:flex tw:flex-col tw:gap-6">
            <Card>
              <Card.Content className="tw:flex tw:flex-col tw:gap-3">
                <code className="tw:font-mono tw:text-xs tw:break-all">{app.id}</code>
                <span className="tw:text-xs tw:text-muted-foreground">
                  {app.vendor.name ?? app.vendor.id}
                </span>
                <PublishRejectActions app={app} />
              </Card.Content>
            </Card>

            <Card>
              <Card.Header>
                <Card.Title as="h2">Facts</Card.Title>
              </Card.Header>
              <Card.Content className="tw:flex tw:flex-col tw:gap-2 tw:text-sm">
                <PublicFlags app={app} />
                <hr className="tw:border-border" />
                {/* Admin-only facts — none of these appear on the public detail page. */}
                <Fact label="Version" value={app.version} />
                <Fact label="Added by" value={app.createdBy} />
                <Fact label="Repository" value={app.repository?.uri} mono />
                <Fact label="Tag" value={app.repository?.tag} mono />
                <Fact label="Required memory" value={app.requiredMemory ?? 'Platform default'} />
                <Fact
                  label="Process timeout"
                  value={
                    app.processTimeout === undefined || app.processTimeout === null
                      ? 'Platform default'
                      : `${app.processTimeout} s`
                  }
                />
                <div className="tw:flex tw:items-center tw:gap-2">
                  <span className="tw:text-muted-foreground">Forwards token</span>
                  <Badge color={app.forwardToken === true ? 'amber' : 'gray-inverse'}>
                    {app.forwardToken === true ? 'Yes' : 'No'}
                  </Badge>
                </div>
              </Card.Content>
            </Card>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

function Fact({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}) {
  if (value === undefined || value === null || value === '') return null;

  return (
    <div className="tw:flex tw:flex-wrap tw:items-baseline tw:gap-2">
      <span className="tw:text-muted-foreground">{label}</span>
      <span className={mono ? 'tw:font-mono tw:text-xs tw:break-all' : undefined}>{value}</span>
    </div>
  );
}
